import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import AutocompleteSelect from "../../../AutocompleteSelect";

export default function InlineNoteEditor({
  entry,
  selectedNoteVersion,
  disabled = false,
  showEditButton = false,
  canEdit = false,
  onSave,
  serviceOptionsData,
}) {
  const { options: serviceOptions, loading: serviceOptionsLoading } =
    serviceOptionsData;
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [savedData, setSavedData] = useState({});

  const isReceptionNote = entry?.note_type === "receptionist";
  const viewingSpecificVersion = !!selectedNoteVersion;

  const display = useMemo(() => {
    if (viewingSpecificVersion) {
      const v = selectedNoteVersion;
      return {
        note_content: v?.note_content ?? "",
        doctor_required:
          typeof v?.doctor_required === "boolean"
            ? v.doctor_required
            : undefined,
        service_id: v?.service_id ?? null,
        reservation_count:
          typeof v?.reservation_count === "number" ? v.reservation_count : null,
        reservation_unit: v?.reservation_unit ?? null,
      };
    }
    return {
      note_content: entry?.note_content ?? entry?.note ?? "",
      doctor_required:
        typeof entry?.doctor_required === "boolean"
          ? entry.doctor_required
          : undefined,
      service_id: entry?.service_id ?? entry?.service?.id ?? null,
      reservation_count:
        typeof entry?.reservation_count === "number"
          ? entry.reservation_count
          : null,
      reservation_unit: entry?.reservation_unit ?? null,
    };
  }, [viewingSpecificVersion, selectedNoteVersion, entry]);

  const serviceName = serviceOptions.find(
    (s) => s.id === display.service_id,
  )?.name;

  useEffect(() => {
    const src = viewingSpecificVersion
      ? (selectedNoteVersion ?? {})
      : (entry ?? {});
    const initial = {
      note_content: src.note_content || "",
    };
    if (isReceptionNote) {
      initial.doctor_required = src.doctor_required ?? false;
      initial.service_id = src.service_id || "";
      initial.reservation_count =
        typeof src.reservation_count === "number" ? src.reservation_count : 0;
      initial.reservation_unit = src.reservation_unit || "days";
    }
    setFormData(initial);
    setSavedData(initial);
  }, [viewingSpecificVersion, selectedNoteVersion, entry, isReceptionNote]);

  const toggleEdit = () => setIsEditing(true);

  const handleSave = async () => {
    if (!onSave) return;
    try {
      const previous_id = selectedNoteVersion?.version_id ?? entry?.id ?? null;

      await onSave({
        previous_id,
        note_content: formData.note_content ?? "",
        doctor_required: !!formData.doctor_required,
        service_id: formData.service_id || null,
        reservation_count:
          typeof formData.reservation_count === "number"
            ? formData.reservation_count
            : 0,
        reservation_unit: formData.reservation_unit || "days",
      });

      setSavedData(formData);
      setIsEditing(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancel = () => {
    setFormData(savedData);
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const isEmptyNote = !formData.note_content?.trim();

  function czPlural(wordSet, n) {
    if (n === 1) return wordSet[0];
    if (n >= 2 && n <= 4) return wordSet[1];
    return wordSet[2];
  }

  function formatReservation(count, unit) {
    // Explicitly handle null/undefined for both fields
    if (unit === "earliestAvailableDate") return "Nejbližší volný termín";
    if (count == null && (unit == null || unit === ""))
      return "Nepotřebuje termín";
    if (count == null || count === 0) return "Nepotřebuje termín";

    if (unit === "days")
      return `${count} ${czPlural(["den", "dny", "dnů"], count)}`;
    if (unit === "weeks")
      return `${count} ${czPlural(["týden", "týdny", "týdnů"], count)}`;
    if (unit === "months")
      return `${count} ${czPlural(["měsíc", "měsíce", "měsíců"], count)}`;
    return `${count ?? ""} ${unit ?? ""}`.trim();
  }

  return (
    <Box sx={{ position: "relative" }}>
      {!disabled && !isEditing && showEditButton && canEdit && (
        <Box sx={{ position: "absolute", top: 0, right: "-1rem", zIndex: 2 }}>
          <Tooltip title="Upravit">
            <IconButton onClick={toggleEdit} sx={{ color: "primary.main" }}>
              <FontAwesomeIcon icon={faPenToSquare} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {isEditing ? (
        <>
          <TextField
            label="Poznámka"
            value={formData.note_content || ""}
            onChange={(e) => handleChange("note_content", e.target.value)}
            multiline
            rows={isReceptionNote ? 3 : 6}
            fullWidth
            sx={{ mb: isReceptionNote ? 2 : 0 }}
          />

          {isReceptionNote && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.doctor_required}
                    onChange={(e) =>
                      handleChange("doctor_required", e.target.checked)
                    }
                  />
                }
                label="Vyžaduje lékaře"
              />

              <Box sx={{ minWidth: 200 }}>
                <AutocompleteSelect
                  label="Služba"
                  options={serviceOptions}
                  loading={serviceOptionsLoading}
                  value={serviceOptions.find(
                    (s) => s.id === formData.service_id,
                  )}
                  onChange={(value) => {
                    setFormData((p) => ({ ...p, service_id: value?.id }));
                  }}
                />
              </Box>

              <TextField
                size="small"
                type="number"
                label="Počet"
                value={
                  typeof formData.reservation_count === "number"
                    ? formData.reservation_count
                    : 0
                }
                onChange={(e) =>
                  handleChange("reservation_count", Number(e.target.value))
                }
                sx={{ width: 110 }}
                inputProps={{ min: 0 }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Jednotka</InputLabel>
                <Select
                  label="Jednotka"
                  value={formData.reservation_unit || "days"}
                  onChange={(e) =>
                    handleChange("reservation_unit", e.target.value)
                  }
                >
                  <MenuItem value="days">Dny</MenuItem>
                  <MenuItem value="weeks">Týdny</MenuItem>
                  <MenuItem value="months">Měsíce</MenuItem>
                  <MenuItem value="earliestAvailableDate">
                    Nejbližší volný termín
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<FontAwesomeIcon icon={faTimes} />}
            >
              Zrušit
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isReceptionNote && isEmptyNote}
              startIcon={<FontAwesomeIcon icon={faSave} />}
            >
              Uložit
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
            <strong>Poznámka:</strong> {display.note_content?.trim() || "---"}
          </Typography>

          {entry?.note_type === "receptionist" && (
            <>
              {(serviceName || display.service_id) && (
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <strong>Služba:</strong> {serviceName ?? "—"}
                </Typography>
              )}

              {display.service_id != null && (
                <>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong>Rezervace:</strong>{" "}
                    {formatReservation(
                      display.reservation_count,
                      display.reservation_unit,
                    )}
                  </Typography>

                  {typeof display.doctor_required === "boolean" && (
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      <strong>Vyžaduje lékaře:</strong>{" "}
                      {display.doctor_required ? "Ano" : "Ne"}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
